import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import GlobalFeedback from "../../../../global/GlobalFeedback";
import VisitorForm from "./VisitorForm";
import { getVisitorById, updateVisitor } from "../../api/visitorsApi";
import { visitorToFormValues } from "../../utils/visitorValidation";
import { ensureCsrfToken, safeErrorText } from "../../utils/visitorHelpers";

/**
 * Inline visitor edit — same form as full page, used on register/control (like call-logs edit).
 */
export default function VisitorEditPanel({
  visitorId,
  visitor: visitorProp = null,
  limitedEdit = false,
  onCancel,
  onSuccess,
}) {
  const [visitor, setVisitor] = useState(visitorProp);
  const [loading, setLoading] = useState(!visitorProp);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const load = useCallback(async () => {
    if (!visitorId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getVisitorById(visitorId);
      setVisitor(res?.data || null);
    } catch (err) {
      setError(safeErrorText(err, "Failed to load visitor"));
      setVisitor(null);
    } finally {
      setLoading(false);
    }
  }, [visitorId]);

  useEffect(() => {
    if (visitorProp) {
      setVisitor(visitorProp);
      setLoading(false);
      return;
    }
    load();
  }, [visitorProp, load]);

  const initialValues = useMemo(
    () => (visitor ? visitorToFormValues(visitor) : undefined),
    [visitor],
  );

  const handleSubmit = async (payload) => {
    if (!visitor?.id) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const csrfToken = await ensureCsrfToken(getCsrfToken);
      const res = await updateVisitor(visitor.id, payload, csrfToken);
      onSuccess?.(res?.message || "Visitor updated successfully");
    } catch (err) {
      setSubmitError(safeErrorText(err, "Failed to update visitor"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className="visitor-table-loading">Loading visitor…</p>;
  }

  if (!visitor) {
    return <GlobalFeedback type="error" text={error || "Visitor not found"} role="alert" />;
  }

  return (
    <div>
      <GlobalFeedback type="error" text={error} role="alert" />
      <VisitorForm
        mode="edit"
        limitedEdit={limitedEdit}
        visitorCode={visitor.visitor_code}
        initialValues={initialValues}
        personToMeetName={visitor.person_to_meet_name}
        personToMeetDepartment={visitor.person_to_meet_department}
        approvedByName={visitor.approved_by_name}
        approvedByDepartment={visitor.approved_by_department}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        submitting={submitting}
        serverError={submitError}
      />
    </div>
  );
}
